// @flow
import * as React from 'react';
import classNames from '@ta/common.classnames';
import decodeString from '@ta/common.decode';
import styles from './ObfuscatedLink.css';

/** The component props for a <ObfuscatedLink> */
export type Props = {|
  /** The browser window target ('_self' for same tab, '_blank' for new tab) */
  target?: '_blank' | '_self',

  /** A click event handler to be called prior to window.open. The original click event is not propagated */
  onClick?: (SyntheticMouseEvent<HTMLElement>) => mixed,

  /** The contained children within the link */
  children?: React.Node,

  /** Additional classes you'd like to add */
  className?: string,

  /** The encoded link generated using UserInterfaceUtil::obfuscateWord.
   * Used as the value for data-encoded-url, which can be read by ClickTracker and decoded client-side. */
  encodedUrl?: string,

  /** Any additional URL Arguments that should be appended to an encodedUrl after it is decoded. */
  urlArgs?: Object,

  /** Render with a span instead of a div when you don't want a block-level element. */
  useSpan?: boolean,

  /** optional fragment that goes to the very end of url (following a hash #) */
  fragment?: string,
|};

/**
 * An SEO obfuscated link, Base64 encoded using UserInterfaceUtil::obfuscateWord
 */
class ObfuscatedLink extends React.Component<Props> {
  static defaultProps = {
    target: '_self',
    children: null,
    className: undefined,
    useSpan: false,
    fragment: '',
  };

  addQueryParamsAndfragment = (decodedUrl: string) => {
    let finalUrl = decodedUrl;
    if (this.props.urlArgs) {
      const { urlArgs } = this.props;
      const [url, urlParams] = decodedUrl.split('?');

      const searchParams = new URLSearchParams(urlParams);

      Object.keys(urlArgs).forEach((key: string) => {
        searchParams.set(key, urlArgs[key]);
      });

      finalUrl = `${url}?${searchParams.toString()}`;
    }

    finalUrl = `${finalUrl}${this.props.fragment ? `#${this.props.fragment}` : ''}`;
    return finalUrl;
  };

  openLink = () => {
    let decodedUrl = this.props.encodedUrl ? decodeString(this.props.encodedUrl) : '';

    if (decodedUrl !== '') {
      decodedUrl = this.addQueryParamsAndfragment(decodedUrl);
      // eslint-disable-next-line no-restricted-globals
      const newWindow = window.open(decodedUrl, this.props.target);
      // popup blockers will cause newWindow to be null or undefined
      if (newWindow) {
        newWindow.opener = null;
      }
    }

    // blank url should no-op, rather than open an empty tab
  };

  handleClick = (event: SyntheticMouseEvent<HTMLElement>) => {
    if (this.props.onClick) {
      this.props.onClick(event);
    }
    this.openLink();
  };

  render() {
    return React.createElement(
      this.props.useSpan ? 'span' : 'div',
      {
        className: classNames(styles.link, this.props.className),
        onClick: this.handleClick,
        'data-encoded-url': this.props.encodedUrl,
      },
      this.props.children,
    );
  }
}

export default ObfuscatedLink;
